import React, { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/brand/Logo";
import {
  LayoutDashboard, Contact2, TrendingUp, StickyNote, Repeat,
  Globe, Smartphone, Palette, Brush, Search, MapPin,
  FileText, FileSpreadsheet, Receipt, History,
  Server, Globe2, Lock, BellRing,
  Share2, CalendarDays, LineChart,
  Wallet, Shirt, Printer,
  Sparkles, LifeBuoy, ListChecks,
  Users, ShieldCheck, Plug, Bell, Building2, CreditCard, Boxes, Star, MessageSquare, CalendarClock,
  Settings, User as UserIcon, PieChart, FolderKanban, Kanban, FolderOpen, Activity,
  X,
} from "lucide-react";

const buildGroups = (t, role) => {
  if (role === "client") {
    return [
      { key: "portal", label: t("portal.title"), items: [
        { key: "portal", to: "/portal", icon: LayoutDashboard, label: t("sidebar.dashboard"), end: true },
        { key: "portal-projects", to: "/portal/projects", icon: FolderKanban, label: t("portal.projects") },
        { key: "portal-invoices", to: "/portal/invoices", icon: FileText, label: t("portal.invoices") },
        { key: "portal-billing", to: "/portal/billing", icon: CreditCard, label: "Billing & Payments" },
        { key: "portal-support", to: "/portal/support", icon: LifeBuoy, label: t("portal.support") },
        { key: "portal-ai", to: "/portal/ai", icon: Sparkles, label: t("portal.ai") },
        { key: "portal-domains", to: "/portal/domains", icon: Globe2, label: t("portal.domains") },
        { key: "portal-hosting", to: "/portal/hosting", icon: Server, label: t("portal.hosting") },
        { key: "portal-files", to: "/portal/files", icon: FileSpreadsheet, label: t("portal.files") },
        { key: "portal-reports", to: "/portal/reports", icon: LineChart, label: t("portal.reports") },
        { key: "profile", to: "/app/profile", icon: UserIcon, label: t("sidebar.profile") },
      ]},
    ];
  }
  return [
    { key: "root", items: [
      { key: "dashboard", to: "/app", icon: LayoutDashboard, label: t("sidebar.dashboard"), end: true },
    ]},
    { key: "crm", label: "CRM", items: [
      { key: "leads", to: "/app/leads", icon: TrendingUp, label: t("sidebar.leads") },
      { key: "crm", to: "/app/crm", icon: Contact2, label: t("sidebar.clients") },
      { key: "followups", to: "/app/followups", icon: Repeat, label: "Follow-ups" },
      { key: "notes", to: "/app/notes", icon: StickyNote, label: "Notes" },
    ]},
    { key: "projects", label: t("sidebar.projects"), items: [
      { key: "projects", to: "/app/projects", icon: FolderKanban, label: "All projects", end: true },
      { key: "projects-kanban", to: "/app/projects/kanban", icon: Kanban, label: "Kanban board" },
      { key: "projects-website", to: "/app/projects/website", icon: Globe, label: "Website" },
      { key: "projects-mobile", to: "/app/projects/mobile_app", icon: Smartphone, label: "Mobile app" },
      { key: "projects-branding", to: "/app/branding", icon: Palette, label: "Branding" },
      { key: "projects-graphic", to: "/app/graphic-design", icon: Brush, label: "Graphic design" },
      { key: "projects-seo", to: "/app/seo", icon: Search, label: "SEO" },
      { key: "projects-gbp", to: "/app/gbp", icon: MapPin, label: "Google Business" },
      { key: "files", to: "/app/files", icon: FolderOpen, label: "File manager" },
    ]},
    { key: "billing", label: "Billing", items: [
      { key: "billing-center", to: "/app/billing", icon: CreditCard, label: "Billing & Renewals" },
      { key: "quotes", to: "/app/quotes", icon: FileText, label: t("sidebar.quotes") },
      { key: "invoices", to: "/app/invoices", icon: FileSpreadsheet, label: t("sidebar.invoices") },
      { key: "subscriptions", to: "/app/subscriptions", icon: Receipt, label: t("sidebar.subscriptions") },
      { key: "payments", to: "/app/payments", icon: History, label: "Payment history" },
    ]},
    { key: "hosting", label: "Hosting", items: [
      { key: "domains", to: "/app/domains", icon: Globe2, label: t("sidebar.domains") },
      { key: "ssl", to: "/app/ssl", icon: Lock, label: t("sidebar.ssl") },
      { key: "hosting", to: "/app/hosting", icon: Server, label: "Hosting accounts" },
      { key: "renewals", to: "/app/renewals", icon: BellRing, label: "Renewal reminders" },
    ]},
    { key: "marketing", label: "Marketing", items: [
      { key: "social", to: "/app/social", icon: Share2, label: t("sidebar.social") },
      { key: "content", to: "/app/content", icon: CalendarDays, label: "Content calendar" },
      { key: "analytics", to: "/app/analytics", icon: LineChart, label: t("sidebar.analytics") },
      { key: "trusted-clients", to: "/app/trusted-clients", icon: Star, label: "Client Marquee" },
      { key: "testimonials", to: "/app/testimonials", icon: MessageSquare, label: "Reviews & Testimonials" },
      { key: "scheduling", to: "/app/scheduling", icon: CalendarClock, label: "Callbacks & Strategy Calls" },
    ]},
    { key: "store", label: "Store", items: [
      { key: "nfc", to: "/app/nfc", icon: Wallet, label: t("sidebar.nfc") },
      { key: "uniforms", to: "/app/uniforms", icon: Shirt, label: t("sidebar.uniforms") },
      { key: "printing", to: "/app/printing", icon: Printer, label: "Printing services" },
    ]},
    { key: "productivity", label: "Productivity", items: [
      { key: "calendar", to: "/app/calendar", icon: CalendarDays, label: t("sidebar.calendar") },
      { key: "tasks", to: "/app/tasks", icon: ListChecks, label: t("sidebar.tasks") },
      { key: "support", to: "/app/support", icon: LifeBuoy, label: t("sidebar.support") },
      { key: "ai", to: "/app/ai", icon: Sparkles, label: t("sidebar.ai") },
    ]},
    { key: "reports", label: "Reports", items: [
      { key: "reports", to: "/app/reports", icon: PieChart, label: "Reports" },
    ]},
    { key: "team", label: "Team", items: [
      { key: "users", to: "/app/users", icon: Users, label: "Employees" },
      { key: "roles", to: "/app/roles", icon: ShieldCheck, label: t("sidebar.roles") },
      { key: "activity", to: "/app/activity", icon: Activity, label: "Activity log" },
    ]},
    { key: "platform", label: t("sidebar.groups.platform"), items: [
      { key: "companies", to: "/app/companies", icon: Building2, label: t("sidebar.companies") },
      { key: "clients-portal-users", to: "/app/clients", icon: Contact2, label: "Portal users" },
      { key: "plans", to: "/app/plans", icon: CreditCard, label: t("sidebar.plans") },
      { key: "integrations", to: "/app/integrations", icon: Plug, label: t("sidebar.integrations") },
      { key: "notifications", to: "/app/notifications", icon: Bell, label: t("sidebar.notifications") },
      { key: "future", to: "/app/future", icon: Boxes, label: t("sidebar.future_modules") },
    ]},
    { key: "account", label: t("sidebar.groups.account"), items: [
      { key: "settings", to: "/app/settings", icon: Settings, label: t("sidebar.settings") },
      { key: "profile", to: "/app/profile", icon: UserIcon, label: t("sidebar.profile") },
    ]},
  ];
};

export function SidebarContent({ onNavigate }) {
  const { t } = useLang();
  const { user } = useAuth();
  const groups = buildGroups(t, user?.role);
  const [query, setQuery] = useState("");

  // Flatten items with their group label for search results
  const allItems = useMemo(
    () => groups.flatMap((g) => g.items.map((it) => ({ ...it, groupLabel: g.label || "" }))),
    [groups]
  );

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return null;
    return allItems.filter(
      (it) =>
        (it.label || "").toLowerCase().includes(q) ||
        (it.groupLabel || "").toLowerCase().includes(q) ||
        (it.key || "").toLowerCase().includes(q)
    );
  }, [q, allItems]);

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      <div className="px-5 py-5 border-b border-slate-800 shrink-0">
        <Logo />
      </div>
      {/* Quick search */}
      <div className="px-3 pt-3 pb-1 shrink-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search modules…"
            data-testid="sidebar-search-input"
            className="w-full h-9 pl-8 pr-8 rounded-md bg-slate-900/70 border border-slate-800 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:bg-slate-900 outline-none transition-colors"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="sidebar-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 w-5 h-5 grid place-items-center rounded"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4" data-testid="sidebar-nav">
        {filtered ? (
          <div data-testid="sidebar-search-results">
            <div className="overline px-2 mb-1.5">
              {filtered.length} {filtered.length === 1 ? "result" : "results"}
            </div>
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid="sidebar-search-empty">
                No modules match “{query}”.
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map((it) => (
                  <NavLink
                    key={it.key}
                    to={it.to}
                    end={it.end}
                    onClick={() => { setQuery(""); onNavigate?.(); }}
                    data-testid={`sidebar-search-result-${it.key}`}
                    className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                  >
                    <it.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate flex-1">{it.label}</span>
                    {it.groupLabel && (
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-2 shrink-0">
                        {it.groupLabel}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.key}>
              {g.label && <div className="overline px-2 mb-1.5">{g.label}</div>}
              <div className="space-y-1">
                {g.items.map((it) => (
                  <NavLink key={it.key} to={it.to} end={it.end} onClick={() => onNavigate?.()}
                    data-testid={`sidebar-${it.key}`}
                    className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
                    <it.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{it.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))
        )}
      </nav>
      <div className="px-4 py-3 border-t border-slate-800 shrink-0">
        <div className="text-[11px] text-slate-500">v0.4 · Business OS</div>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside data-testid="sidebar" className="hidden lg:flex flex-col w-64 shrink-0 border-r border-slate-800 h-screen sticky top-0">
      <SidebarContent />
    </aside>
  );
}
